// RigSentinel — Inheritance Trace (developer diagnostic)
//
// Route: /dev/inheritance-trace
//
// Purpose: Engineering Review 1.0 — verifies that every field designed to
// inherit automatically passes ALL 8 inheritance checkpoints. Strictly
// diagnostic; may be removed or hidden after the review closes.
//
// Columns:
//   1. Authoritative Owner
//   2. Published by Owner
//   3. Registered in Resolver
//   4. Available in Session
//   5. Retrieved during Re-pull
//   6. Stored in Frozen Snapshot
//   7. Bound to UI
//   8. Updates after Re-pull

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Search, Check, X, AlertTriangle, Layers } from "lucide-react";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import {
  auditInheritance, CHECKPOINTS, CHECKPOINT_LABELS,
} from "@/lib/rce/inheritance";

const SHORT_LABELS = {
  authoritativeOwner:     "Owner",
  publishedByOwner:       "Published",
  registeredInResolver:   "Resolver",
  availableInSession:     "Session",
  retrievedDuringRePull:  "Re-pull",
  storedInFrozenSnapshot: "Snapshot",
  boundToUI:              "UI Bind",
  updatesAfterRePull:     "Re-pull Δ",
};

function Pill({ ok, na }) {
  if (na) {
    return (
      <span
        data-testid="ih-pill-na"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full"
        style={{ background: "rgba(107,114,128,0.16)", color: "#9CA3AF" }}
        aria-label="not applicable"
        title="Operational reading — exempt from inheritance"
      >
        —
      </span>
    );
  }
  if (ok) {
    return (
      <span
        data-testid="ih-pill-yes"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full"
        style={{ background: "rgba(34,197,94,0.16)", color: "#22C55E" }}
        aria-label="yes"
      >
        <Check className="w-3 h-3" />
      </span>
    );
  }
  return (
    <span
      data-testid="ih-pill-no"
      className="inline-flex items-center justify-center w-5 h-5 rounded-full"
      style={{ background: "rgba(239,68,68,0.16)", color: "#EF4444" }}
      aria-label="no"
    >
      <X className="w-3 h-3" />
    </span>
  );
}

function Row({ result, expanded, onToggle }) {
  const inheritChecks = Object.entries(result.checks).filter(([k]) => k !== "authoritativeOwner");
  const allPass = inheritChecks.every(([, v]) => v);
  const isOpOnly = result.operationalOnly;
  return (
    <>
      <tr
        data-testid={`ih-row-${result.fieldId}`}
        data-allpass={allPass ? "true" : "false"}
        data-op-only={isOpOnly ? "true" : "false"}
        onClick={onToggle}
        className="cursor-pointer hover:bg-[rgba(255,255,255,0.025)]"
      >
        <td className="px-3 py-2 align-top">
          <div className="text-[12px] font-semibold text-[var(--rs-text-1)] flex items-center gap-2">
            {result.fieldId}
            {isOpOnly && (
              <span className="text-[8px] uppercase tracking-[0.16em] px-1 py-0.5 rounded border border-[var(--rs-text-3)] text-[var(--rs-text-3)]">
                Operational
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono text-[var(--rs-text-3)]">{result.inputPath}</div>
        </td>
        <td className="px-2 py-2 align-top text-[11px] text-[var(--rs-text-2)] min-w-[140px]">
          {result.expectedOwner}
        </td>
        {CHECKPOINTS.slice(1).map((c) => (
          <td key={c} className="px-1 py-2 align-top text-center">
            <Pill ok={result.checks[c]} na={isOpOnly} />
          </td>
        ))}
      </tr>
      {expanded && (
        <tr data-testid={`ih-detail-${result.fieldId}`}>
          <td colSpan={9} className="px-3 pb-3">
            <div
              className="rounded-md border p-3 text-[11px]"
              style={{ borderColor: "var(--rs-border)", background: "rgba(10,11,14,0.55)" }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <KV k="RCE path"        v={result.rcePath || "— (not registered)"} mono />
                <KV k="Kind"            v={result.details.kind} mono />
                <KV k="Resolved value"  v={result.details.resolvedValue || "—"} mono />
                <KV k="Confidence"      v={result.details.resolvedConfidence} mono />
                <KV k="Source"          v={result.details.resolvedSource
                  ? `${result.details.resolvedSource.module}${result.details.resolvedSource.label ? " · " + result.details.resolvedSource.label : ""}`
                  : "—"} />
                <KV k="Notes"           v={result.notes || "—"} />
              </div>

              <div data-testid={`ih-reasons-${result.fieldId}`}>
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-[var(--rs-text-3)] mb-1.5">
                  Reasons (per checkpoint)
                </div>
                <ul className="space-y-1">
                  {CHECKPOINTS.map((c) => {
                    const ok = result.checks[c];
                    return (
                      <li key={c} className="flex items-start gap-2">
                        <span className="mt-[3px]"><Pill ok={ok} na={isOpOnly && c !== "authoritativeOwner"} /></span>
                        <span className="text-[var(--rs-text-2)]">
                          <span className="font-semibold text-[var(--rs-text-1)]">{CHECKPOINT_LABELS[c]}</span>
                          <span className="text-[var(--rs-text-3)]"> — </span>
                          {result.reasons[c] || "—"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--rs-text-3)]">{k}</div>
      <div className={`text-[11px] ${mono ? "font-mono" : ""} text-[var(--rs-text-1)] break-all`}>{v}</div>
    </div>
  );
}

export default function InheritanceTrace() {
  const navigate = useNavigate();
  const { session } = useEngineeringSession();
  const [filter, setFilter]               = useState("");
  const [showBrokenOnly, setShowBrokenOnly] = useState(false);
  const [expanded, setExpanded]           = useState({});

  const ctx = useMemo(() => ({
    documentId:    undefined,
    projectId:     session?.projectId,
    rigId:         session?.rigId,
    wellId:        session?.wellId,
    bhaId:         session?.bhaId,
    drillStringId: session?.drillStringId,
    mudProgramId:  session?.mudProgramId,
  }), [session]);

  const audit = useMemo(() => auditInheritance(ctx), [ctx]);

  const filtered = useMemo(() => {
    const out = {};
    const q = filter.trim().toLowerCase();
    for (const [mod, rows] of Object.entries(audit.byModule)) {
      const keep = rows.filter((r) => {
        if (showBrokenOnly) {
          if (r.operationalOnly) return false;
          const inheritChecks = Object.entries(r.checks).filter(([k]) => k !== "authoritativeOwner");
          if (inheritChecks.every(([, v]) => v)) return false;
        }
        if (!q) return true;
        return (
          r.fieldId.toLowerCase().includes(q) ||
          r.inputPath.toLowerCase().includes(q) ||
          (r.rcePath || "").toLowerCase().includes(q) ||
          (r.expectedOwner || "").toLowerCase().includes(q)
        );
      });
      if (keep.length > 0) out[mod] = keep;
    }
    return out;
  }, [audit, filter, showBrokenOnly]);

  return (
    <div className="pb-8" data-testid="inheritance-trace">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          data-testid="ih-back"
          onClick={() => navigate(-1)}
          className="btn-ghost h-9 w-9 rounded-md inline-flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading font-black text-xl">Inheritance Trace</h1>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
            Engineering Review 1.0 · diagnostic
          </div>
        </div>
        <Link
          to="/dev/context-inspector"
          data-testid="ih-link-inspector"
          className="text-[11px] text-amber-400 hover:underline"
        >
          Context Inspector →
        </Link>
      </div>

      {/* Session summary */}
      <div
        data-testid="ih-session-strip"
        className="rounded-md border p-3 mb-3 text-[11px] text-[var(--rs-text-2)]"
        style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
      >
        <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-1">Active Engineering Session</div>
        {session ? (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
            <li><b>Project:</b> {session.projectId || "—"}</li>
            <li><b>Rig:</b> {session.rigId || "—"}</li>
            <li><b>Well:</b> {session.wellId || "—"}</li>
            <li><b>BHA:</b> {session.bhaId || "—"}</li>
            <li><b>Drill String:</b> {session.drillStringId || "—"}</li>
            <li><b>Mud Program:</b> {session.mudProgramId || "—"}</li>
          </ul>
        ) : (
          <div>No active session — start one via the Active Operation pill.</div>
        )}
      </div>

      {/* Summary */}
      <div
        data-testid="ih-summary"
        className="rounded-md border p-3 mb-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]"
        style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
      >
        <KV k="Total fields"     v={audit.summary.totalFields} />
        <KV k="Inheritable"      v={audit.summary.inheritableFields} />
        <KV k="Fully wired"      v={`${audit.summary.fullyWiredFields} / ${audit.summary.inheritableFields}`} />
        <KV k="Coverage"         v={`${audit.summary.coveragePct}%`} />
        <KV k="Broken"           v={audit.summary.brokenFields.length} />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className="flex-1 min-w-[200px]">
          <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-1">Filter</div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--rs-text-3)]" />
            <input
              data-testid="ih-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="field name, input path, rce path, owner…"
              className="w-full h-9 pl-7 pr-2 rounded border bg-[rgba(10,11,14,0.75)] text-[12px] text-[var(--rs-text-1)]"
              style={{ borderColor: "var(--rs-border)" }}
            />
          </div>
        </label>
        <label className="inline-flex items-center gap-1.5 mt-5 text-[11px] text-[var(--rs-text-2)] cursor-pointer">
          <input
            data-testid="ih-broken-only"
            type="checkbox"
            checked={showBrokenOnly}
            onChange={(e) => setShowBrokenOnly(e.target.checked)}
            className="accent-amber-400"
          />
          Show broken only
        </label>
      </div>

      {/* Per-module tables */}
      {Object.entries(filtered).map(([moduleName, rows]) => (
        <div
          key={moduleName}
          data-testid={`ih-module-${moduleName}`}
          className="rounded-md border overflow-hidden mb-4"
          style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
        >
          <div
            className="flex items-center gap-2 px-3 py-2 border-b text-[11px] text-[var(--rs-text-1)]"
            style={{ borderColor: "var(--rs-border)" }}
          >
            <Layers className="w-3.5 h-3.5 text-amber-400" />
            <span className="font-semibold">{moduleName}</span>
            <span className="text-[var(--rs-text-3)]">· {rows.length} field{rows.length === 1 ? "" : "s"}</span>
            <span className="ml-auto text-[10px] text-[var(--rs-text-3)] uppercase tracking-[0.16em]">
              Click any row to expand
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-[0.16em] text-[var(--rs-text-3)] border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-2 py-2">{CHECKPOINT_LABELS.authoritativeOwner}</th>
                  {CHECKPOINTS.slice(1).map((c) => (
                    <th key={c} className="px-1 py-2 text-center" title={CHECKPOINT_LABELS[c]}>
                      {SHORT_LABELS[c]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row
                    key={r.fieldId}
                    result={r}
                    expanded={!!expanded[r.fieldId]}
                    onToggle={() => setExpanded((s) => ({ ...s, [r.fieldId]: !s[r.fieldId] }))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {Object.keys(filtered).length === 0 && (
        <div
          data-testid="ih-empty"
          className="rounded-md border p-4 text-center text-[12px] text-[var(--rs-text-3)]"
          style={{ borderColor: "var(--rs-border)" }}
        >
          No rows match the current filter.
        </div>
      )}

      {/* Footer disclaimer */}
      <div
        className="mt-4 p-3 rounded-md border text-[10.5px] text-[var(--rs-text-3)] flex items-start gap-2"
        style={{ borderColor: "var(--rs-border)", background: "rgba(255,179,0,0.04)" }}
      >
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          This diagnostic exists strictly for <strong>Engineering Review 1.0</strong>. It is read-only
          and can be removed or hidden once the review closes. The audit reads the live RCE registry,
          deriver pipeline, snapshot consumes-list, and curated UI/Re-pull metadata in <code className="text-amber-300">lib/rce/inheritance.js</code>.
        </div>
      </div>
    </div>
  );
}
